import React from 'react'

import styles from './_floating-action-bar.module.scss'
import {
  Button,
  DropdownMenu,
  Icon,
  type IconStringList,
  useIsMobileView,
} from '../../module'
import { type DropdownAction } from '../DropdownMenu/DropdownMenu'
import { c } from '../../translations/LibraryTranslationService'

/**
 * Props for the FloatingActionBar component
 */
interface FloatingActionBarProps {
  /** Array of selected items/boxes */
  selectedItemCount: number
  /** Total number of items in the dataset */
  totalItemCount: number
  /** Optional array of action buttons to display in the bar. */
  actions: DropdownAction[]
  /** Optional callback function to execute when the close button is clicked */
  onClose?: () => void
  /** Optional prop to add a test id to the FloatingActionBar for QA testing */
  qaTestId?: string
}

const FloatingActionBar = ({
  selectedItemCount,
  totalItemCount,
  actions,
  onClose,
  qaTestId = 'floating-action-bar',
}: FloatingActionBarProps) => {
  const isMobile = useIsMobileView()
  const visibleActions = isMobile ? actions.slice(0, 1) : actions.slice(0, 4)
  const hiddenActions = isMobile
    ? actions.slice(1)
    : actions.length > 4
      ? actions.slice(4)
      : []

  return (
    <div
      className={`${styles.floatingBar} ${isMobile ? styles.mobile : ''}`}
      qa-test-id={qaTestId}
    >
      <div className={styles.floatingBarContent}>
        <span className={styles.closeButton} onClick={onClose}>
          <Icon icon='x' color='white' iconSize={isMobile ? '12px' : '16px'} />
        </span>
        <span className={styles.fwBold}>
          {c('selectedCount', {
            selectedItemCount,
            totalItemCount,
          })}
        </span>
      </div>
      <div className={styles.floatingBarContent}>
        <div className={styles.actionButtons}>
          <div className={styles.actionButtonsGroup}>
            {visibleActions.map((action, index) => (
              <Button
                key={index}
                as='unstyled'
                onClick={action.callout}
                disabled={action.disabled?.value}
                className={styles.actionButton}
              >
                {action.icon ? (
                  <Icon
                    icon={action.icon as IconStringList}
                    color={action.disabled?.value ? 'gray' : 'white'}
                    iconSize={isMobile ? '12px' : '20px'}
                  />
                ) : null}
                {action.text}
              </Button>
            ))}
          </div>
          {hiddenActions.length > 0 ? (
            <div className={styles.hiddenActionsContainer}>
              <span className={styles.divider} />
              <DropdownMenu
                actions={hiddenActions as DropdownAction[]}
                trigger={
                  <Button as='unstyled'>
                    <Icon
                      icon='dotsVertical'
                      iconSize={isMobile ? '16px' : '20px'}
                      color='white'
                      className={styles.verticalActionButton}
                    />
                  </Button>
                }
                popoverProps={{ position: 'auto-end' }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default FloatingActionBar
